import {
  ACTION_TACHE_COLLECTEUR,
  ACTIONS_TACHES_JUSTIFICATIFS,
  BORNES_CHANGE,
  type ComptesJustificatifs,
  ETATS_A_JUSTIFIER,
  type EtatOperationPortail,
  ETATS_RECHERCHE_DEMANDEE,
  formaterJour,
  formaterMontant,
  formaterPeriode,
  FOURNISSEURS_PORTAIL,
  HEURE_PASSAGE_MENSUEL,
  heureAParis,
  jourAParis,
  JOUR_PASSAGE_MENSUEL,
  lancementPossible,
  LIBELLES_MOTIF_DEPOT,
  MAX_ECRITURES_PAR_PASSAGE,
  modeDeDepot,
  type ModeJustificatifs,
  type ModeTransmission,
  type RelectureJustificatifs,
  type SourcePiece,
  type StatutCollecte,
  type StatutJustificatif,
} from '@admin-pmvb/core'

// Bilan des justificatifs d'une connexion (Tiime) : dans quelle liste va chaque opération, et
// les textes de l'en-tête du passage. Sans React, pour être testé seul. La page montre ce que la
// règle ferait, un humain décide (écarter, rouvrir, reconnaître, contester, choisir le justificatif
// d'une opération : « C'est celle-ci », décision de Pierre-Marie du 13/10/2026), demande une
// recherche dans les mails ou un passage ; aucun bouton n'écrit chez le portail. Étape d'écriture :
// le titulaire ouvre l'écriture (le titulaire ou un administrateur la coupe) ; l'agent du Mac
// dépose puis relie les sûres, choisies comprises, et la page montre ce qu'il a écrit (déposés non
// reliés, à vérifier).

/**
 * Listes du bilan, dans l'ordre de la page : d'abord ce qui attend un regard, puis, repliées,
 * les opérations écartées ou hors champ.
 */
export const LISTES_BILAN = [
  'relierait',
  'reliee',
  'a_trancher',
  'deja',
  'mails',
  'a_fournir',
  'ecartee',
  'hors_champ',
] as const
export type ListeBilan = (typeof LISTES_BILAN)[number]

/** Listes repliées à l'ouverture de la page. */
export const LISTES_REPLIEES: ReadonlySet<ListeBilan> = new Set(['ecartee', 'hors_champ'])

/** Listes des opérations terminées : le bilan n'en rend que les plus récentes. */
export const LISTES_TERMINEES: ReadonlySet<ListeBilan> = new Set([
  'reliee',
  'deja',
  'ecartee',
  'hors_champ',
])

/**
 * Opérations terminées que le bilan rend au plus, les plus récentes (`MAX_TERMINALES_BILAN` du
 * service) : à ce nombre, les listes terminées sont peut-être coupées.
 */
export const TERMINEES_MONTREES = 100

/** Les listes terminées sont-elles peut-être coupées (le serveur en a rendu le maximum) ? */
export function termineesCoupees(listes: Record<ListeBilan, readonly unknown[]>): boolean {
  let n = 0
  for (const l of LISTES_TERMINEES) n += listes[l].length
  return n >= TERMINEES_MONTREES
}

/**
 * La liste d'une opération, d'après son état au plan : une opération à fournir dont la recherche
 * a retrouvé des pièces dans les mails a sa liste ; hors fenêtre, aucune (le bilan ne la rend pas).
 */
export function listeDe(op: {
  etat: EtatOperationPortail
  piecesMails: readonly string[]
}): ListeBilan | null {
  switch (op.etat) {
    case 'sure':
      return 'relierait'
    case 'reliee':
      return 'reliee'
    case 'proposee':
      return 'a_trancher'
    case 'justifiee':
      return 'deja'
    case 'a_fournir':
      return op.piecesMails.length > 0 ? 'mails' : 'a_fournir'
    case 'ecartee':
      return 'ecartee'
    case 'hors_champ':
      return 'hors_champ'
    case 'hors_fenetre':
      return null
  }
}

/** Les opérations du bilan rangées par liste, dans l'ordre où le serveur les rend. */
export function listesDuBilan<
  T extends { etat: EtatOperationPortail; piecesMails: readonly string[] },
>(operations: readonly T[]): Record<ListeBilan, T[]> {
  const listes = Object.fromEntries(LISTES_BILAN.map((l) => [l, [] as T[]])) as Record<
    ListeBilan,
    T[]
  >
  for (const op of operations) {
    const l = listeDe(op)
    if (l) listes[l].push(op)
  }
  return listes
}

/**
 * Ce que la page sait du passage pour titrer ses listes : le portail, l'écriture ouverte (les
 * sûres seront reliées au prochain passage) et, faux seulement chez un portail qui exige une
 * catégorie de dépôt introuvable à ce passage (Tiime), `depotPermis` (serveur,
 * `depotPermisParCategorie`) : ses sûres ne se déposent pas seules.
 */
export type ContexteListes = { portail: string; ecritureOuverte: boolean; depotPermis?: boolean }

// ---------------------------------------------------------------------------------------------
// Les quatre onglets de la page (refonte du 14/10/2026)
// ---------------------------------------------------------------------------------------------

/** Les onglets de la page, dans leur ordre ; chacun regroupe des listes du bilan. */
export const ONGLETS_BILAN = ['a_justifier', 'a_relier', 'justifiees', 'ecartees'] as const
export type OngletBilan = (typeof ONGLETS_BILAN)[number]

/**
 * Les listes de chaque onglet : à justifier (à choisir, retrouvées dans les mails, à fournir),
 * prêtes à relier (sûres), justifiées (reliées par l'agent, déjà chez le portail), écartées (par
 * un humain, ou hors champ d'après la règle).
 */
export const LISTES_DES_ONGLETS: Readonly<Record<OngletBilan, readonly ListeBilan[]>> = {
  a_justifier: ['a_trancher', 'mails', 'a_fournir'],
  a_relier: ['relierait'],
  justifiees: ['reliee', 'deja'],
  ecartees: ['ecartee', 'hors_champ'],
}

/** L'onglet d'une liste. */
export function ongletDe(l: ListeBilan): OngletBilan {
  return ONGLETS_BILAN.find((o) => LISTES_DES_ONGLETS[o].includes(l))!
}

/** Les opérations rangées par onglet, chacune avec sa liste, la plus récente en premier. */
export function ongletsDuBilan<
  T extends { etat: EtatOperationPortail; piecesMails: readonly string[]; date: string },
>(operations: readonly T[]): Record<OngletBilan, { op: T; liste: ListeBilan }[]> {
  const onglets = Object.fromEntries(ONGLETS_BILAN.map((o) => [o, []])) as unknown as Record<
    OngletBilan,
    { op: T; liste: ListeBilan }[]
  >
  for (const op of operations) {
    const l = listeDe(op)
    if (l) onglets[ongletDe(l)].push({ op, liste: l })
  }
  for (const o of ONGLETS_BILAN)
    onglets[o].sort((a, b) => (a.op.date < b.op.date ? 1 : a.op.date > b.op.date ? -1 : 0))
  return onglets
}

/** Combien d'opérations attendent leur justificatif (l'onglet « À justifier », `ETATS_A_JUSTIFIER`). */
export function compteAJustifier(ops: readonly { etat: EtatOperationPortail }[]): number {
  return ops.filter((op) => (ETATS_A_JUSTIFIER as readonly string[]).includes(op.etat)).length
}

export function titreOnglet(o: OngletBilan): string {
  switch (o) {
    case 'a_justifier':
      return 'À justifier'
    case 'a_relier':
      return 'Prêtes à relier'
    case 'justifiees':
      return 'Justifiées'
    case 'ecartees':
      return 'Écartées'
  }
}

/** Ce que dit un onglet, à côté du titre de la liste. */
export function aideOnglet(o: OngletBilan, c: ContexteListes): string {
  switch (o) {
    case 'a_justifier':
      return 'pas de pièce sûre : choisir, chercher ou déposer'
    case 'a_relier':
      if (c.depotPermis === false) return 'sûres, mais aucun dépôt possible sans catégorie'
      return c.ecritureOuverte
        ? 'une seule pièce correspond : l’agent la dépose et la relie au prochain passage'
        : 'une seule pièce correspond : l’agent la reliera seul une fois l’écriture ouverte'
    case 'justifiees':
      return `rien à faire : un justificatif est dans ${c.portail}`
    case 'ecartees':
      return 'sans justificatif attendu'
  }
}

/**
 * D'où vient le justificatif d'une opération justifiée : mis chez le portail à la main (ou par une
 * autre voie), ou déposé par l'agent du Mac ; null hors de l'onglet « Justifiées ».
 */
export function origineJustifiee(l: ListeBilan, portail: string): string | null {
  if (l === 'deja') return `Mis dans ${portail} à la main`
  if (l === 'reliee') return 'Déposée par l’agent'
  return null
}

// ---------------------------------------------------------------------------------------------
// L'écart entre une pièce et l'écriture du cabinet : deux tuiles, quatre tons
// ---------------------------------------------------------------------------------------------

/** Ton d'un écart : identique (vert), proche (vert-olive), à vérifier (ambre), loin (rouge). */
export type TonEcart = 'identique' | 'proche' | 'ambre' | 'loin'

/**
 * Seuils des tons : le montant identique au centime et la date à trois jours près, verts ; à 50
 * centimes ou 1 % près et à sept jours près, vert-olive ; à 10 % près et à quinze jours près,
 * ambre ; au-delà, rouge.
 */
export const SEUILS_ECART = {
  procheCts: 50,
  procheMilliemes: 10,
  ambreMilliemes: 100,
  identiqueJours: 3,
  procheJours: 7,
  ambreJours: 15,
} as const

/** Ton de l'écart de montant `ecartCts` sur une écriture de `referenceCts` (valeur absolue). */
export function tonEcartMontant(ecartCts: number, referenceCts: number): TonEcart {
  const e = Math.abs(ecartCts)
  const ref = Math.abs(referenceCts)
  if (e === 0) return 'identique'
  if (e <= SEUILS_ECART.procheCts || e * 1000 <= ref * SEUILS_ECART.procheMilliemes) return 'proche'
  if (e * 1000 <= ref * SEUILS_ECART.ambreMilliemes) return 'ambre'
  return 'loin'
}

/** Ton de l'écart de date, en jours. */
export function tonEcartDate(ecartJours: number): TonEcart {
  const e = Math.abs(ecartJours)
  if (e <= SEUILS_ECART.identiqueJours) return 'identique'
  if (e <= SEUILS_ECART.procheJours) return 'proche'
  if (e <= SEUILS_ECART.ambreJours) return 'ambre'
  return 'loin'
}

const MOIS_COURTS = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
]

const JOUR_ISO = /^(\d{4})-(\d{2})-(\d{2})$/

/** « 2026-09-12 » devient « 12 sept. » (avec l'année : « 12 sept. 2026 ») ; tel quel s'il est illisible. */
export function jourCourt(j: string, annee = false): string {
  const m = JOUR_ISO.exec(j)
  if (!m) return j
  return `${Number(m[3])} ${MOIS_COURTS[Number(m[2]) - 1] ?? m[2]}${annee ? ` ${m[1]}` : ''}`
}

/**
 * Un instant en court, à Paris : « 14 oct., 10:53 », avec l'année quand elle n'est pas celle de
 * `maintenant` (« 30 déc. 2025, 18:02 ») ; tel quel s'il est illisible.
 */
export function instantCourt(instant: string, maintenant: Date): string {
  const d = new Date(instant)
  if (Number.isNaN(d.getTime())) return instant
  const jour = jourAParis(d)
  const annee = jour.slice(0, 4) !== jourAParis(maintenant).slice(0, 4)
  return `${jourCourt(jour, annee)}, ${heureAParis(d)}`
}

/** Jours de `de` à `a` (AAAA-MM-JJ) : positif quand `a` est après `de` ; null si illisible. */
export function joursEntre(de: string, a: string): number | null {
  const x = JOUR_ISO.exec(de)
  const y = JOUR_ISO.exec(a)
  if (!x || !y) return null
  const t = (m: RegExpExecArray) => Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Math.round((t(y) - t(x)) / 86_400_000)
}

/** Le signe moins typographique (U+2212), comme dans la maquette. */
const MOINS = '−'

const signe = (n: number) => (n > 0 ? '+' : n < 0 ? MOINS : '')

/** Un montant signé, « 0,00 € », « +1,00 € » ou « −54,60 € ». */
export function montantSigne(cts: number, devise = 'EUR'): string {
  return `${signe(cts)}${formaterMontant(Math.abs(cts), devise)}`
}

/** Un pourcentage à la française, une décimale au plus (« 10 », « 0,5 »). */
function pourcent(milliemesDixiemes: number): string {
  return (Math.round(milliemesDixiemes) / 10).toLocaleString('fr-FR', { maximumFractionDigits: 1 })
}

/**
 * L'écart d'une pièce candidate à une écriture du cabinet, pour ses deux tuiles et son rang :
 * - montant : pièce moins écriture (en valeur absolue), son ton, le texte « +1,00 € », la ligne
 *   « 9,99 € vs 9,99 € (+x %) » et la barre qui part du centre (pleine moitié à 20 %) ; null quand
 *   la pièce n'a pas de montant lu ou est dans une autre devise (le taux effectif le dit) ;
 * - date : jours de l'écriture à la pièce (« −1 j » : la pièce est datée de la veille), son ton,
 *   la ligne « 11 sept. vs 12 sept. » et la place de la pastille sur un axe de ±31 jours ;
 * - rang : |écart montant| / montant × 100 + |écart jours| (une pièce sans montant comparable
 *   compte 100) ; la plus proche a le plus petit.
 */
export function ecartCandidate(
  op: { date: string; montantCts: number; devise: string },
  piece: { jour: string; montantCts: number | null; devise: string },
) {
  const ref = Math.abs(op.montantCts)
  const comparable = piece.montantCts !== null && piece.devise === op.devise
  let montant: {
    ecartCts: number
    ton: TonEcart
    texte: string
    detail: string
    barre: { gauche: number; largeur: number }
  } | null = null
  if (comparable) {
    const ecartCts = Math.abs(piece.montantCts!) - ref
    // En dixièmes de pour cent : 1 000 pour 100 %.
    const part = ref === 0 ? (ecartCts === 0 ? 0 : 1000) : (ecartCts * 1000) / ref
    const largeur = Math.min(50, (Math.abs(part) / 200) * 50)
    montant = {
      ecartCts,
      ton: ref === 0 && ecartCts !== 0 ? 'loin' : tonEcartMontant(ecartCts, ref),
      texte: montantSigne(ecartCts, op.devise),
      detail: `${formaterMontant(Math.abs(piece.montantCts!), op.devise)} vs ${formaterMontant(ref, op.devise)}${
        ecartCts === 0 ? '' : ` (${signe(ecartCts)}${pourcent(Math.abs(part))} %)`
      }`,
      barre: {
        gauche: part >= 0 ? 50 : 50 - largeur,
        largeur: ecartCts === 0 ? 0 : Math.max(largeur, 2),
      },
    }
  }
  const jours = joursEntre(op.date, piece.jour)
  const date =
    jours === null
      ? null
      : {
          ecartJours: jours,
          ton: tonEcartDate(jours),
          texte: jours === 0 ? '0 j' : `${signe(jours)}${Math.abs(jours)} j`,
          detail: `${jourCourt(piece.jour)} vs ${jourCourt(op.date)}`,
          position: 50 + Math.max(-1, Math.min(1, jours / 31)) * 46,
        }
  const rang =
    (montant && ref > 0 ? (Math.abs(montant.ecartCts) / ref) * 100 : 100) + Math.abs(jours ?? 31)
  return { montant, date, rang }
}

/**
 * Les candidates d'une opération, de la plus proche à la moins proche (`ecartCandidate`, rang
 * croissant) ; à rang égal, l'ordre de la règle.
 */
export function candidatesTriees<
  C extends { pieceId: string },
  P extends { jour: string; montantCts: number | null; devise: string },
>(
  op: { date: string; montantCts: number; devise: string },
  candidates: readonly C[],
  piece: (id: string) => P | undefined,
): (C & { ecart: ReturnType<typeof ecartCandidate> | null })[] {
  return candidates
    .map((c, i) => {
      const p = piece(c.pieceId)
      return { c: { ...c, ecart: p ? ecartCandidate(op, p) : null }, i }
    })
    .sort((a, b) => (a.c.ecart?.rang ?? 1e9) - (b.c.ecart?.rang ?? 1e9) || a.i - b.i)
    .map((x) => x.c)
}

// ---------------------------------------------------------------------------------------------
// Les deux recherches du panneau : les boîtes mails, un connecteur
// ---------------------------------------------------------------------------------------------

/** Le bouton « Scanner les boîtes mails », avec le nombre de boîtes que le lecteur voit. */
export function libelleScanMails(o: { boites: number | null; enCours: boolean }): string {
  const n = o.boites
  if (o.enCours)
    return n === null || n === 0
      ? 'Scan des boîtes mails…'
      : `Scan ${n > 1 ? `des ${n} boîtes` : 'de la boîte'}…`
  return n !== null && n > 0
    ? `Scanner ${n > 1 ? `les ${n} boîtes mails` : 'la boîte mail'}`
    : 'Scanner les boîtes mails'
}

/** Une connexion de documents telle que la liste des connecteurs la montre. */
export type ConnecteurVu = {
  active: boolean
  robot: boolean
  etat: string
  sansReleve: string | null
  collecte: { statut: string } | null
  defi: { id: string } | null
  dernierSuccesLe: string | null
}

/**
 * L'état d'un connecteur dans la liste « Récupérer via » : désactivé, collecte en cours,
 * vérification requise (« Reconnecter X » : un défi ouvert mène à son écran, sinon la collecte à
 * la demande fait reconnecter le titulaire), relève impossible (le motif), en erreur, relevé le
 * jour dit, jamais relevé. `lancer` : la collecte à la demande peut partir (`relancePossible`).
 */
export function etatConnecteur(
  c: ConnecteurVu,
  o: { dernierSucces: string | null },
): { texte: string; reconnecter: boolean; lancer: boolean; ton: 'neutre' | 'ambre' } {
  // Le défi d'un connecteur ne dit pas s'il est en direct : rien à reprendre ici.
  const lancer = relancePossible({ ...c, defi: null })
  if (!c.active) return { texte: 'Désactivée', reconnecter: false, lancer, ton: 'neutre' }
  if (c.collecte)
    return {
      texte: c.collecte.statut === 'en_file' ? 'Collecte lancée' : 'Collecte en cours',
      reconnecter: c.defi !== null,
      lancer,
      ton: c.defi ? 'ambre' : 'neutre',
    }
  if (c.defi || c.etat === 'verification_requise')
    return { texte: 'Vérification requise', reconnecter: true, lancer, ton: 'ambre' }
  if (c.sansReleve) return { texte: c.sansReleve, reconnecter: false, lancer, ton: 'ambre' }
  if (c.etat === 'erreur' || c.etat === 'bloquee')
    return {
      texte: c.etat === 'erreur' ? 'En erreur' : 'Bloquée',
      reconnecter: false,
      lancer,
      ton: 'ambre',
    }
  return {
    texte: o.dernierSucces ? `Relevé le ${o.dernierSucces}` : 'Jamais relevé',
    reconnecter: false,
    lancer,
    ton: 'neutre',
  }
}

/** Le nom court d'un connecteur, d'après le libellé de son fournisseur (« Ulys », « ChatGPT »). */
export function nomConnecteur(libelleFournisseur: string): string {
  return libelleFournisseur.split(' (')[0] ?? libelleFournisseur
}

/** D'où vient une pièce, telle que le bilan la rend. */
export type OriginePiece = {
  source: SourcePiece
  boite: string | null
  portail: string | null
  collecteId: string | null
}

/**
 * D'où vient une pièce candidate, en clair, et son icône : relevée par un connecteur, arrivée
 * dans une boîte, déposée dans l'appli, envoyée en photo, ajoutée par l'assistant, reçue d'une
 * plateforme, version remplie.
 */
export function texteOrigine(o: OriginePiece): { texte: string; icone: NomIconeOrigine } {
  switch (o.source) {
    case 'portail':
      return {
        texte: o.portail ? `Relevée via ${o.portail}` : 'Relevée par un connecteur',
        icone: 'hub',
      }
    case 'gmail':
      return { texte: o.boite ? `Arrivée dans ${o.boite}` : 'Arrivée par mail', icone: 'mail' }
    case 'depot':
      return { texte: 'Déposée dans l’appli', icone: 'upload_file' }
    case 'photo':
      return { texte: 'Envoyée en photo', icone: 'photo_camera' }
    case 'mcp':
      return { texte: 'Ajoutée par l’assistant', icone: 'smart_toy' }
    case 'plateforme':
      return { texte: 'Reçue d’une plateforme', icone: 'download' }
    case 'remplissage':
      return { texte: 'Version remplie', icone: 'edit_note' }
  }
}

/** Les icônes d'origine (noms de `Icone`), sans dépendre du composant React. */
export type NomIconeOrigine =
  'hub' | 'mail' | 'upload_file' | 'photo_camera' | 'smart_toy' | 'download' | 'edit_note'

/**
 * Le bouton principal de « Récupérer via » : le connecteur proposé, à reconnecter, ou aucun. Une
 * collecte relève toutes les factures du connecteur : `ecritures` dit combien d'écritures à
 * justifier l'attendent (au-delà d'une, elles sont nommées sur le bouton).
 */
export function libelleRecuperer(
  c: { nom: string; reconnecter: boolean; enCours: boolean; ecritures?: number } | null,
) {
  if (!c) return 'Récupérer via connecteur'
  if (c.enCours) return `Récupération via ${c.nom}…`
  if (c.reconnecter) return `Reconnecter ${c.nom}`
  return (c.ecritures ?? 0) > 1
    ? `Récupérer via ${c.nom} (${c.ecritures} écritures)`
    : `Récupérer via ${c.nom}`
}

/**
 * Combien d'écritures à justifier (à choisir, à fournir) attendent ce connecteur : celui choisi
 * pour l'écriture, sinon celui que son libellé propose (`connecteurDe`).
 */
export function ecrituresDuConnecteur<O>(
  lignes: readonly { op: O; liste: ListeBilan }[],
  connexionId: string,
  connecteurDe: (op: O) => string | null,
): number {
  return lignes.filter(
    (l) =>
      (l.liste === 'a_trancher' || l.liste === 'a_fournir') && connecteurDe(l.op) === connexionId,
  ).length
}

/**
 * Ce que dit une récupération, d'après sa collecte (lancée depuis le panneau de n'importe quelle
 * écriture du connecteur, ou déjà ouverte) : lancée, en cours, en attente d'une vérification, puis
 * la facture trouvée (une candidate de l'opération vient de ce connecteur), des documents
 * rapportés pas encore lus, rien au montant et à la date de l'écriture, ou l'échec. `enCours` :
 * la collecte est encore ouverte.
 */
export function texteRecuperation(r: {
  nom: string
  statut: StatutCollecte | null
  documentsNouveaux: number | null
  erreur: string | null
  trouvee: boolean
  montant: string
  jour: string
}): { texte: string; ton: 'ok' | 'neutre' | 'alerte'; enCours: boolean } {
  if (r.trouvee)
    return {
      texte: `Facture trouvée dans ${r.nom}, ajoutée ci-dessous.`,
      ton: 'ok',
      enCours: false,
    }
  switch (r.statut) {
    case null:
    case 'en_file':
      return {
        texte: `Récupération via ${r.nom} lancée…`,
        ton: 'neutre',
        enCours: true,
      }
    case 'en_cours':
    case 'reprise':
      return { texte: `Récupération via ${r.nom} en cours…`, ton: 'neutre', enCours: true }
    case 'attente_humain':
      return {
        texte: `${r.nom} demande une vérification : le titulaire de la connexion y répond dans l’appli.`,
        ton: 'alerte',
        enCours: true,
      }
    case 'terminee':
      return r.documentsNouveaux
        ? {
            texte: `${r.nom} a rapporté ${pluriel(r.documentsNouveaux, 'document', 'documents')} : une facture rejoint les pièces ci-dessous une fois lue et rattachée, au prochain calcul.`,
            ton: 'neutre',
            enCours: false,
          }
        : {
            // « 6 oct. » finit déjà par un point.
            texte: `Rien dans ${r.nom} à ${r.montant} autour du ${r.jour}${r.jour.endsWith('.') ? '' : '.'}`,
            ton: 'neutre',
            enCours: false,
          }
    default:
      return {
        texte: `Récupération via ${r.nom} ${LIBELLES_STATUT_PASSAGE[r.statut]}${r.erreur ? ` : ${r.erreur}` : '.'}`,
        ton: 'alerte',
        enCours: false,
      }
  }
}

/**
 * Relecture du bilan : toutes les cinq secondes pendant la lecture d'un passage ou tant qu'une
 * recherche dans les mails demandée attend son résultat, toutes les trente secondes tant qu'un
 * passage attend son ordinateur, jamais pendant un défi (le titulaire doit d'abord répondre).
 */
export function intervalleBilan(
  b:
    | {
        passage: { statut: StatutCollecte } | null
        operations: readonly { rechercheDemandeeLe: string | null }[]
      }
    | undefined,
): number | false {
  const statut = b?.passage?.statut
  if (statut === 'en_cours') return 5000
  if (b?.operations.some((op) => op.rechercheDemandeeLe !== null)) return 5000
  if (statut === 'en_file' || statut === 'reprise') return 30_000
  return false
}

/** Titre d'une liste ; jamais « Relierait seul » quand aucun dépôt n'est possible. */
export function titreListe(l: ListeBilan, o: ContexteListes) {
  switch (l) {
    case 'relierait':
      if (o.depotPermis === false) return 'Sûres, sans dépôt possible'
      return o.ecritureOuverte ? 'À relier au prochain passage' : 'Relierait seul'
    case 'reliee':
      return 'Relié'
    case 'a_trancher':
      return 'À choisir'
    case 'deja':
      return `Déjà dans ${o.portail}`
    case 'mails':
      return 'Retrouvée dans les mails'
    case 'a_fournir':
      return 'À fournir'
    case 'ecartee':
      return 'Écartées'
    case 'hors_champ':
      return 'Hors champ'
  }
}

/** Ce que dit une liste, sous son titre. */
export function aideListe(l: ListeBilan, o: ContexteListes) {
  switch (l) {
    case 'relierait':
      if (o.depotPermis === false)
        return `Correspondance sûre, mais la catégorie des justificatifs est introuvable ou ambiguë dans ${o.portail} : aucun dépôt n’y part seul. La déposer et la relier à la main, ou faire corriger la catégorie : le passage suivant la reconnaîtra.`
      return o.ecritureOuverte
        ? `Correspondance sûre : le prochain passage relit l’opération, dépose la pièce dans ${o.portail} et l’y relie.`
        : `Correspondance sûre : un passage en écriture déposerait la pièce et la relierait seul. À blanc, rien n’est écrit dans ${o.portail}.`
    case 'reliee':
      return `Pièces déposées et reliées dans ${o.portail} par l’appli.`
    case 'a_trancher':
      return `La règle ne choisit pas seule le justificatif : « C’est celle-ci » sur la bonne pièce, et l’agent du Mac la dépose et la relie au prochain passage en écriture, avec les mêmes contrôles qu’une correspondance sûre. Quand le débit du relevé est apparié, le même geste fait son rapprochement bancaire avec le paiement prévu de la pièce. On peut aussi la relier à la main dans ${o.portail} : le passage suivant la reconnaît.`
    case 'deja':
      return `Opérations déjà justifiées dans ${o.portail} : la pièce de l’appli reconnue y est marquée « déjà dans ${o.portail} », jamais déposée.`
    case 'mails':
      return 'Pièces retrouvées dans les mails : elles entrent par le circuit habituel et comptent au prochain calcul.'
    case 'a_fournir':
      return 'Aucune pièce de l’appli en vue : déposer la facture dans l’appli, ou dire qu’aucun justificatif n’est attendu.'
    case 'ecartee':
      return 'Aucun justificatif attendu, d’après un humain.'
    case 'hors_champ':
      return 'Aucune pièce attendue d’après la règle (montant nul, rejet, virement interne, organisme, crédit).'
  }
}

const pluriel = (n: number, un: string, plusieurs: string) => `${n} ${n > 1 ? plusieurs : un}`

/**
 * Taux effectif en millièmes (centimes de l'opération pour 1 000 centimes de la pièce), écrit à
 * la française : 891 donne « 0,891 ».
 */
export function tauxEffectif(milliemes: number): string {
  const n = Math.abs(Math.trunc(milliemes))
  return `${milliemes < 0 ? '-' : ''}${Math.floor(n / 1000)},${String(n % 1000).padStart(3, '0')}`
}

/** Bande de change d'une devise (« 0,850 à 0,940 »), null pour une devise sans bande. */
export function bandeDeChange(devise: string): string | null {
  if (!Object.hasOwn(BORNES_CHANGE, devise)) return null
  const b = BORNES_CHANGE[devise]
  return b ? `${tauxEffectif(b.min)} à ${tauxEffectif(b.max)}` : null
}

/** Écart entre la pièce et l'opération (positif : l'opération suit la pièce). */
export function texteEcart(ecartJours: number): string {
  if (ecartJours === 0) return 'le jour de la pièce'
  const n = Math.abs(ecartJours)
  return `${pluriel(n, 'jour', 'jours')} ${ecartJours > 0 ? 'après' : 'avant'} la pièce`
}

/** Le résumé d'un passage en quelques nombres (« 312 opérations lues · 9 relieraient seules »). */
export function resumeComptes(
  b: {
    lues: number | null
    etats: ComptesJustificatifs | null
    marquees?: number | null
  },
  o: { ecritureOuverte: boolean; depotPermis?: boolean },
): string[] {
  const parties: string[] = []
  if (b.lues !== null) parties.push(pluriel(b.lues, 'opération lue', 'opérations lues'))
  const e = b.etats
  if (!e) return parties
  if (e.sure > 0)
    parties.push(
      o.depotPermis === false
        ? `${pluriel(e.sure, 'sûre', 'sûres')}, sans dépôt possible`
        : o.ecritureOuverte
          ? `${e.sure} à relier`
          : pluriel(e.sure, 'relierait seule', 'relieraient seules'),
    )
  if (e.reliee > 0) parties.push(pluriel(e.reliee, 'reliée', 'reliées'))
  if (e.proposee > 0) parties.push(`${e.proposee} à choisir`)
  if (e.a_fournir > 0) parties.push(`${e.a_fournir} à fournir`)
  if (e.justifiee > 0) parties.push(pluriel(e.justifiee, 'déjà justifiée', 'déjà justifiées'))
  if (b.marquees) parties.push(pluriel(b.marquees, 'pièce reconnue', 'pièces reconnues'))
  return parties
}

export const LIBELLES_MODE_PASSAGE: Record<ModeJustificatifs, string> = {
  a_blanc: 'À blanc',
  ecriture: 'Écriture',
}

/** Où en est un passage (une collecte de justificatifs). */
export const LIBELLES_STATUT_PASSAGE: Record<StatutCollecte, string> = {
  en_file: 'en attente de l’ordinateur',
  en_cours: 'en cours',
  attente_humain: 'attend le titulaire',
  reprise: 'reprise',
  terminee: 'terminé',
  echec: 'en échec',
  bloquee: 'bloqué',
  expiree: 'expiré',
  annulee: 'annulé',
}

/** La sonde de relecture d'une opération, lue par l'agent. */
export function texteRelecture(r: RelectureJustificatifs): string {
  if (r === 'ok') return 'Relecture d’une opération : possible.'
  if (r === 'indisponible')
    return 'Relecture d’une opération indisponible : aucune écriture tant que l’adaptateur n’a pas de repli.'
  return 'Relecture d’une opération : pas essayée.'
}

/**
 * La catégorie de dépôt des justificatifs ; `undefined` : pas encore lue. Sans catégorie, rien ne
 * se dépose chez un portail qui l'exige (`categorieDepotExigee`, Tiime) ; chez un portail qui n'en
 * a pas (Dougs), son absence est normale et la page n'en dit rien (null).
 */
export function texteCategorie(
  c: string | null | undefined,
  o: { exigee: boolean },
): string | null {
  if (c === undefined) return null
  if (c === null)
    return o.exigee
      ? 'Catégorie des justificatifs introuvable ou ambiguë : aucun dépôt possible.'
      : null
  return 'Catégorie des justificatifs trouvée.'
}

/** « 09:00 » devient « 9 h », « 09:30 » devient « 9 h 30 ». */
export function heureFrancaise(hhmm: string): string {
  const [h, m] = hhmm.split(':')
  return `${Number(h)} h${m && m !== '00' ? ` ${m}` : ''}`
}

/** Quand passe le rapprochement mensuel (constantes du core). */
export const TEXTE_PASSAGE_MENSUEL = `Passage du mois à lancer dès le ${JOUR_PASSAGE_MENSUEL} à ${heureFrancaise(HEURE_PASSAGE_MENSUEL)} (page Collecteurs)`

/**
 * Message après « Rapprocher maintenant » : le titulaire lance un rapprochement (à blanc tant
 * que l'écriture n'est pas ouverte) ; une autre personne, un passage à blanc qui attend le
 * titulaire.
 */
export function messageRapprochement(
  c: {
    libelle: string
    titulaireId: string
    titulaire: string
    ecritureActive: boolean
    passageValideLe: string | null
  },
  o: { moi: string; portail: string },
): string {
  if (c.titulaireId !== o.moi)
    return `Passage à blanc de ${c.libelle} demandé : il partira quand ${c.titulaire} dira « C’est parti ».`
  if (c.ecritureActive && c.passageValideLe !== null)
    return `Rapprochement de ${c.libelle} demandé.`
  return `Passage à blanc de ${c.libelle} demandé : rien ne sera écrit dans ${o.portail}.`
}

/**
 * Nom court du portail où une entité de ce mode dépose ses justificatifs (« Tiime » pour le
 * dépôt direct) ; null pour l'envoi par mail ou « rien à envoyer ».
 */
export function portailDuMode(mode: ModeTransmission): string | null {
  for (const [cle, f] of Object.entries(FOURNISSEURS_PORTAIL))
    if (modeDeDepot(cle) === mode) return f.libelle.split(' (')[0] ?? f.libelle
  return null
}

// ---------------------------------------------------------------------------------------------
// « C'est celle-ci » : le choix d'un humain (décision de Pierre-Marie du 13/10/2026)
// ---------------------------------------------------------------------------------------------

/** Le choix d'une pièce tel que le bilan le rend : la pièce, qui (son nom, s'il est visible), quand. */
export type ChoixOperation = { pieceId: string; par: string | null; le: string | null }

/**
 * Motifs du plan qui retiennent un choix : la pièce présente, non déposable ou déposée ailleurs,
 * l'opération ventilée, pas encore réalisée ou validée chez le cabinet.
 */
const MOTIFS_QUI_RETIENNENT: ReadonlySet<string> = new Set([
  ...Object.keys(LIBELLES_MOTIF_DEPOT),
  'ventilee',
  'provisoire',
  'validee',
])

/**
 * « C'est celle-ci » sur une pièce en vue : une candidate d'une opération à trancher, ni ventilée,
 * ni pas encore réalisée, ni déjà validée chez le cabinet (le serveur refuserait), qui n'est pas
 * déjà la pièce choisie, ni déjà chez le cabinet par une autre voie (la relier à la main). Une
 * pièce non déposable se choisit : le choix attend qu'elle le devienne (payeur indiqué, par
 * exemple).
 */
/** La recherche de pièce s'ouvre sur une écriture à justifier, sans choix, que l'agent peut relier. */
export function peutChercherUnePiece(op: {
  etat: EtatOperationPortail
  ventilee: boolean
  provisoire: boolean
  validee: boolean
  choix: ChoixOperation | null
}): boolean {
  return (
    (op.etat === 'proposee' || op.etat === 'a_fournir') &&
    !op.ventilee &&
    !op.provisoire &&
    !op.validee &&
    op.choix === null
  )
}

/** Ce qu'une recherche peut proposer : une facture rattachée, jamais un doublon. */
export function pieceCherchable(p: {
  type: string
  statut: string
  doublonDeId?: string | null
}): boolean {
  return p.type === 'facture' && p.statut === 'rattachee' && !p.doublonDeId
}

export function peutChoisir(
  op: {
    etat: EtatOperationPortail
    ventilee: boolean
    provisoire: boolean
    validee: boolean
    choix: ChoixOperation | null
  },
  piece: { pieceId: string; depot: string | undefined },
): boolean {
  return (
    op.etat === 'proposee' &&
    !op.ventilee &&
    !op.provisoire &&
    !op.validee &&
    op.choix?.pieceId !== piece.pieceId &&
    piece.depot !== 'presente'
  )
}

/**
 * Pourquoi une opération à trancher ne reçoit aucun choix dans l'appli : ventilée, pas encore
 * réalisée ou déjà validée chez le cabinet (`choisirPiece` le refuse) ; null sinon.
 */
export function texteSansChoix(
  op: { etat: EtatOperationPortail; ventilee: boolean; provisoire: boolean; validee: boolean },
  portail: string,
): string | null {
  if (op.etat !== 'proposee') return null
  const raison = op.ventilee
    ? `Ventilée dans ${portail}`
    : op.provisoire
      ? `Pas encore réalisée dans ${portail}`
      : op.validee
        ? `Déjà validée dans ${portail}`
        : null
  return (
    raison &&
    `${raison} : l’appli n’y choisit pas de pièce. La relier à la main dans ${portail} : le passage suivant la reconnaît.`
  )
}

/** Ce que devient le choix d'un humain sur une opération du bilan. */
export type EtatChoix = 'a_relier' | 'en_attente' | 'retenu' | 'caduc'

/**
 * Le choix d'une opération, d'après son état au plan : sûre sur la pièce choisie, l'agent la
 * reliera (`a_relier`) ; encore à trancher avec la pièce parmi ses candidates, le plan ne l'a pas
 * encore vue (un passage en cours : `en_attente`, elle sera reliée de même) ou la retient (motif
 * de dépôt, opération ventilée : `retenu`) ; la pièce plus en vue, ou l'opération à fournir, hors
 * champ : `caduc`. Reliée ou justifiée chez le cabinet, ou sans choix : rien (null).
 */
export function etatChoix(op: {
  etat: EtatOperationPortail
  pieceId: string | null
  choix: ChoixOperation | null
  candidates: readonly { pieceId: string }[]
  motif: string | null
}): EtatChoix | null {
  const c = op.choix
  if (!c || op.etat === 'reliee' || op.etat === 'justifiee') return null
  if (op.etat === 'sure') return op.pieceId === c.pieceId ? 'a_relier' : 'caduc'
  if (op.etat !== 'proposee' || !op.candidates.some((x) => x.pieceId === c.pieceId)) return 'caduc'
  return op.motif !== null && MOTIFS_QUI_RETIENNENT.has(op.motif) ? 'retenu' : 'en_attente'
}

/**
 * Ce que dit le choix d'une opération : qui l'a fait et quand (`le` déjà formaté), et ce qu'il
 * devient (`etatChoix`) ; `libelleMotif` : le libellé du motif du plan, pour un choix retenu.
 */
export function texteChoix(
  op: Parameters<typeof etatChoix>[0],
  o: { ecritureOuverte: boolean; libelleMotif: string | null },
): string | null {
  const etat = etatChoix(op)
  if (!etat || !op.choix) return null
  const qui = `${op.choix.par ?? 'un humain'}${op.choix.le ? ` le ${op.choix.le}` : ''}`
  switch (etat) {
    case 'a_relier':
    case 'en_attente':
      return `Choisie par ${qui} : ${o.ecritureOuverte ? 'reliée par l’agent au prochain passage.' : 'reliée par l’agent au prochain passage en écriture (l’écriture est fermée pour l’instant).'}`
    case 'retenu':
      return `Choisie par ${qui}, mais retenue : ${o.libelleMotif ?? 'à relier à la main'}.`
    case 'caduc':
      return `Choix de ${qui} caduc : la pièce n’est plus en vue pour cette opération. L’annuler, ou choisir une autre pièce.`
  }
}

// ---------------------------------------------------------------------------------------------
// « Chercher dans les mails » et « Rapprocher maintenant » depuis le bilan
// ---------------------------------------------------------------------------------------------

/**
 * « Chercher dans les mails » sur une opération à fournir ou à trancher, quand les verrous de la
 * connexion le permettent (`motifConnexion` nul, rendu par le serveur) et qu'aucune recherche
 * n'est déjà lancée ; le serveur tient en plus les dix minutes entre deux recherches.
 */
export function peutChercher(
  op: { etat: EtatOperationPortail; rechercheDemandeeLe: string | null },
  motifConnexion: string | null,
): boolean {
  return (
    motifConnexion === null &&
    op.rechercheDemandeeLe === null &&
    (ETATS_RECHERCHE_DEMANDEE as readonly string[]).includes(op.etat)
  )
}

/**
 * Où en est la recherche dans les mails d'une opération : lancée (en attente du worker), sinon le
 * résultat de la dernière menée (rien trouvé, pièces retrouvées, aucune boîte). Instants déjà
 * formatés ; null : jamais cherchée.
 */
export function texteRecherche(
  op: {
    rechercheLe: string | null
    rechercheMotif: string | null
    rechercheDemandeeLe: string | null
    piecesMails: readonly string[]
  },
  o: { rechercheLe: string | null; demandeeLe: string | null },
): string | null {
  if (op.rechercheDemandeeLe !== null)
    return `Recherche dans les mails lancée${o.demandeeLe ? ` le ${o.demandeeLe}` : ''} : le résultat s’affiche ici au rafraîchissement.`
  if (op.rechercheLe === null) return null
  const n = op.piecesMails.length
  return [
    `Cherchée dans les mails${o.rechercheLe ? ` le ${o.rechercheLe}` : ''} : ${n > 0 ? `${pluriel(n, 'pièce retrouvée', 'pièces retrouvées')}.` : 'rien trouvé.'}`,
    ...(op.rechercheMotif ? [op.rechercheMotif] : []),
  ].join(' ')
}

/**
 * « Rapprocher maintenant » (« Collecter maintenant » pour un portail de documents) : connexion
 * active, relevée par un robot, rien qui empêche sa relève (`sansReleve`), aucune collecte en
 * cours sauf celle qu'un lancement reprend (`lancementPossible`). Les mêmes conditions dans
 * Collecteurs, Réglages, Portails, et sur la page du bilan ; le serveur décide du reste (passage
 * à blanc pour une autre personne que le titulaire). Sans son déclencheur ni son défi, une
 * collecte ouverte se lit comme lancée par une personne : rien à reprendre.
 */
export function relancePossible(c: {
  active: boolean
  robot: boolean
  sansReleve: string | null
  collecte: { statut: string; declencheur?: string } | null
  defi?: { enDirect?: boolean } | null
}): boolean {
  return lancementPossible({
    ...c,
    collecte: c.collecte && {
      statut: c.collecte.statut as StatutCollecte,
      declencheur: c.collecte.declencheur === 'planifie' ? 'planifie' : 'manuel',
    },
    defi: c.defi ? { enDirect: c.defi.enDirect ?? true } : null,
  })
}

/**
 * Une tâche qui peut mener au bilan d'une connexion de justificatifs : celle d'un collecteur
 * (décision du 02/11/2026), ou les anciennes « justificatifs à choisir, à fournir, à vérifier ».
 */
export const estTacheJustificatifs = (action: string | null): boolean =>
  action === ACTION_TACHE_COLLECTEUR ||
  (ACTIONS_TACHES_JUSTIFICATIFS as readonly string[]).includes(action ?? '')

/**
 * Les connexions de justificatifs dont une tâche parle. La tâche d'un collecteur nomme sa
 * connexion en tête du titre (« Tiime Flow Design : 2 justificatifs à choisir ») : celle-là
 * seulement, et aucune pour un portail de documents. Une ancienne tâche : celles de son entité
 * (une seule par portail) ; s'il y en a plusieurs, celle dont le libellé ouvre le titre, sinon
 * toutes.
 */
export function connexionsDeLaTache<
  C extends { entiteId: string | null; libelle: string; justificatifs: unknown },
>(t: { action: string | null; entiteId: string | null; titre: string }, connexions: readonly C[]) {
  if (!estTacheJustificatifs(t.action) || t.entiteId === null) return []
  const siennes = connexions.filter((c) => c.justificatifs !== null && c.entiteId === t.entiteId)
  const nommees = siennes.filter((c) => t.titre.startsWith(`${c.libelle} : `))
  if (t.action === ACTION_TACHE_COLLECTEUR) return nommees
  if (siennes.length <= 1) return siennes
  return nommees.length > 0 ? nommees : siennes
}

/**
 * Ce qu'un humain peut faire d'une pièce en vue d'une opération déjà justifiée chez le portail :
 * contester la pièce retenue si elle y est reconnue au registre (par la règle ou un humain),
 * reconnaître une candidate encore déposable ; rien pour une pièce déjà chez le cabinet par une
 * autre voie (envoyée par mail, plateforme) ni pour une pièce non déposable : le serveur
 * refuserait.
 */
export function gesteDejaDansLePortail(o: {
  /** La pièce est celle que retient le plan pour l'opération. */
  retenue: boolean
  /** Une ligne `present` du registre la reconnaît sur cette opération. */
  reconnue: boolean
  depot: string | undefined
}): 'contester' | 'reconnaitre' | null {
  if (o.retenue) return o.reconnue ? 'contester' : null
  return o.depot === 'deposable' ? 'reconnaitre' : null
}

/**
 * Le titre de la pièce retenue sur une opération déjà justifiée chez le portail : reconnue au
 * registre ; déposée par l'agent, son arrivée constatée par un humain après un dépôt interrompu
 * (`arrivee`) ; sinon arrivée par une autre voie.
 */
export function titrePieceRetenue(reconnue: boolean, portail: string, arrivee = false): string {
  if (reconnue) return `Reconnue, marquée « déjà dans ${portail} »`
  if (arrivee) return `Déposée par l’agent, arrivée constatée dans ${portail}`
  return 'Déjà chez le cabinet par une autre voie (envoi par mail, plateforme)'
}

/**
 * Qui a demandé le passage : le mois du passage mensuel, l'assistant, le titulaire ou une autre
 * personne. Lu sur le plafond posé à la création : le « C'est parti » du titulaire réécrit le
 * déclencheur (manuel) d'un passage demandé par une autre personne, pas son plafond.
 */
export function texteDeclencheur(p: {
  mensuel: string | null
  viaAssistant: boolean
  plafond: ModeJustificatifs
  declencheur: string
}): string {
  if (p.mensuel) return `passage mensuel de ${formaterPeriode(p.mensuel)}`
  if (p.viaAssistant) return 'demandé via l’assistant'
  if (p.plafond === 'ecriture') return 'demandé par le titulaire'
  return `demandé par une autre personne que le titulaire${p.declencheur === 'manuel' ? ', lancé par le titulaire' : ''}`
}

/** Le conseil d'échec, sauf s'il redit l'erreur montrée juste au-dessus. */
export function conseilDistinct(conseil: string | null, erreur: string | null): string | null {
  return conseil && conseil !== erreur ? conseil : null
}

/**
 * La devise dont le taux effectif d'une pièce est montré : celle que la règle a lue sur
 * l'extraction, si elle diffère de celle de l'opération ; jamais la devise mise par défaut.
 */
export function deviseDuTaux(
  piece: { deviseLue: string | null } | undefined,
  deviseOperation: string,
): string | null {
  return piece?.deviseLue && piece.deviseLue !== deviseOperation ? piece.deviseLue : null
}

// ---------------------------------------------------------------------------------------------
// Étape d'écriture : ouvrir et couper l'écriture de la connexion
// ---------------------------------------------------------------------------------------------

/**
 * Ce que la page propose pour l'écriture, d'après ce que le serveur rend au lecteur
 * (`ecriture.possible`, qui fait foi) : « Ouvrir l'écriture » au titulaire quand tous les
 * verrous sont ouverts, sinon le motif en clair (le premier verrou qui manque, « seul le titulaire »
 * pour un autre) ; « Couper l'écriture » au titulaire ou à un administrateur quand elle est
 * ouverte. Jamais les deux ; rien via l'assistant (le serveur refuse).
 */
export function gestesEcriture(e: {
  active: boolean
  possible: { ouvrir: boolean; couper: boolean; libelle: string | null }
}): { ouvrir: boolean; couper: boolean; motif: string | null } {
  return {
    ouvrir: !e.active && e.possible.ouvrir,
    couper: e.active && e.possible.couper,
    motif: !e.active && !e.possible.ouvrir ? e.possible.libelle : null,
  }
}

/**
 * Les sûres que l'agent écrirait (annoncées par « Ouvrir l'écriture ») : toutes, sauf, quand la
 * catégorie de dépôt manque chez un portail qui l'exige, celles qu'il faudrait d'abord déposer.
 */
export function suresAEcrire(
  ops: readonly { etat: EtatOperationPortail; action: string | null }[],
  o: { depotPermis: boolean },
): number {
  return ops.filter(
    (op) => op.etat === 'sure' && (o.depotPermis || op.action !== 'deposer_et_relier'),
  ).length
}

/**
 * Confirmation d'« Ouvrir l'écriture » : ce que l'agent fera (déposer puis relier les seules
 * correspondances sûres, `sures` au dernier passage, vingt au plus par passage), ce qu'il ne fera
 * jamais, et la coupure toujours possible.
 */
export function confirmationOuvrirEcriture(o: {
  libelle: string
  portail: string
  sures: number
}): string {
  return [
    `Ouvrir l’écriture de ${o.libelle} ?`,
    `À vos prochains passages et au passage mensuel, l’agent du Mac déposera dans ${o.portail} le fichier d’origine de chaque correspondance sûre, puis la reliera à son opération (${o.sures > 0 ? o.sures : 'aucune'} au dernier passage, ${MAX_ECRITURES_PAR_PASSAGE} au plus par passage).`,
    `Rien d’autre n’est écrit dans ${o.portail} : ni catégorie, ni suppression, ni modification.`,
    'L’écriture se coupe à tout moment (« Couper l’écriture »).',
  ].join(' ')
}

/**
 * Confirmation de « Couper l'écriture » : rien de plus n'est écrit, la validation pour écrire
 * tombe (un nouveau passage validé, puis « Ouvrir l'écriture », pour rouvrir) ; la lecture, les
 * tâches à choisir et à fournir et la recherche dans les mails continuent (décision du 13/10/2026).
 */
export function confirmationCouperEcriture(o: { libelle: string; portail: string }): string {
  return [
    `Couper l’écriture de ${o.libelle} ?`,
    `L’agent n’écrira plus rien dans ${o.portail} ; un passage en cours s’arrête avant sa prochaine écriture.`,
    'La validation du passage à blanc tombe : pour rouvrir, un nouveau passage à blanc validé, puis « Ouvrir l’écriture ».',
    'D’ici là, la lecture, le bilan, les tâches « à choisir » et « à fournir », la recherche dans les mails et les dépôts à vérifier continuent.',
  ].join(' ')
}

/**
 * Ce que fait la validation d'un passage à blanc : les tâches s'ouvrent ; écriture fermée, rien
 * n'est écrit ; écriture ouverte, la validation la réarme : les sûres listées seront déposées et
 * reliées au prochain passage.
 */
function effetValidation(o: { portail: string; ecritureOuverte: boolean }): string {
  return o.ecritureOuverte
    ? `l’écriture est ouverte : les correspondances sûres listées seront déposées et reliées dans ${o.portail} au prochain passage.`
    : `rien n’est écrit dans ${o.portail}, l’écriture reste fermée.`
}

/** Confirmation de « Valider ce passage à blanc ». */
export function confirmationValidation(o: {
  libelle: string
  portail: string
  ecritureOuverte: boolean
}): string {
  return `Valider ce passage à blanc de ${o.libelle} ? Les tâches « à choisir » et « à fournir » s’ouvriront ; ${effetValidation(o)}`
}

/** Le texte sous « Valider ce passage à blanc ». */
export function aideValidation(o: { portail: string; ecritureOuverte: boolean }): string {
  return `Après relecture des listes ci-dessous : la validation ouvre les tâches « à choisir » et « à fournir », confiées au titulaire ; ${effetValidation(o)}`
}

/**
 * L'état de l'écriture de la connexion, en lignes : ouverte (par le titulaire) ou fermée, le
 * coupe-circuit du déploiement (`portails.ecritures`), la dernière coupure. Instants déjà formatés.
 */
export function texteEtatEcriture(
  e: { active: boolean; configurees: boolean },
  o: { portail: string; ouverteLe: string | null; coupeeLe: string | null },
): string[] {
  return [
    e.active
      ? `Ouverte${o.ouverteLe ? ` le ${o.ouverteLe}` : ''} par le titulaire : les correspondances sûres sont déposées puis reliées dans ${o.portail}, rien d’autre.`
      : `Fermée : l’agent n’écrit rien dans ${o.portail}.`,
    e.configurees
      ? 'Écritures permises au déploiement (portails.ecritures).'
      : `Écritures coupées au déploiement (portails.ecritures) : ${e.active ? 'aucun passage n’écrit, même écriture ouverte' : 'l’écriture ne s’ouvre pas'}.`,
    ...(o.coupeeLe ? [`Dernière coupure le ${o.coupeeLe}.`] : []),
  ]
}

// ---------------------------------------------------------------------------------------------
// Étape d'écriture : ce que l'agent a écrit
// ---------------------------------------------------------------------------------------------

/**
 * Les lignes du registre d'un passage (`ecritures`, par statut), en quelques nombres : reliées,
 * déposées pas encore reliées, en cours, interrompues, à vérifier, en échec. Ni les pièces
 * reconnues (comptées à part) ni les abandons (rien d'écrit).
 */
export function resumeEcritures(
  e: Partial<Record<StatutJustificatif, number>> | null | undefined,
): string[] {
  if (!e) return []
  const parties: string[] = []
  const n = (s: StatutJustificatif) => e[s] ?? 0
  if (n('relie') > 0) parties.push(pluriel(n('relie'), 'reliée ce passage', 'reliées ce passage'))
  if (n('depose') > 0)
    parties.push(pluriel(n('depose'), 'déposée, pas encore reliée', 'déposées, pas encore reliées'))
  if (n('reserve') > 0) parties.push(pluriel(n('reserve'), 'dépôt en cours', 'dépôts en cours'))
  if (n('incertain') > 0)
    parties.push(pluriel(n('incertain'), 'dépôt interrompu', 'dépôts interrompus'))
  if (n('orphelin') > 0)
    parties.push(pluriel(n('orphelin'), 'liaison à vérifier', 'liaisons à vérifier'))
  if (n('echec') > 0) parties.push(pluriel(n('echec'), 'dépôt en échec', 'dépôts en échec'))
  return parties
}

/**
 * Listes du registre, repliées, après celles des opérations : les documents déposés par l'agent
 * pas encore reliés, et ceux qu'un humain doit vérifier chez le portail.
 */
export const LISTES_REGISTRE = ['deposes', 'a_verifier'] as const
export type ListeRegistre = (typeof LISTES_REGISTRE)[number]

export function titreListeRegistre(l: ListeRegistre): string {
  return l === 'deposes' ? 'Déposés non reliés' : 'À vérifier'
}

export function aideListeRegistre(l: ListeRegistre, o: { portail: string }): string {
  return l === 'deposes'
    ? `Pièces déposées dans ${o.portail} par l’agent du Mac, pas encore reliées à leur opération : le prochain passage en écriture relit le document, puis le relie si la correspondance est encore sûre, ou constate seulement une liaison faite à la main ; jamais redéposées.`
    : `Dépôts de l’agent à vérifier dans ${o.portail} : dépôt interrompu, document relié à aucune opération ou à une autre, dépôt pas encore relié depuis sept jours. Relier à la main dans ${o.portail} ce qui manque : le passage suivant le constate.`
}

/**
 * Pourquoi un document déposé n'a pas été relié (motif gardé sur la ligne orpheline) : relié à
 * une autre opération chez le portail, opération justifiée par autre chose entre-temps, opération
 * qui ne s'y prête plus, ou liaison refusée à chaque essai.
 */
function pourquoiOrphelin(motif: string | null): string {
  if (motif === 'document_lie_ailleurs') return 'mais le document y est relié à une autre opération'
  if (motif === 'operation_justifiee')
    return 'mais l’opération a reçu un autre justificatif entre-temps'
  if (
    motif === 'operation_changee' ||
    motif === 'operation_introuvable' ||
    motif === 'ventilee' ||
    motif === 'provisoire'
  )
    return 'mais l’opération ne se prête plus à une liaison seule (changée, introuvable, ventilée ou pas encore réalisée)'
  if (motif?.startsWith('liaison ')) return 'mais la liaison a été refusée à chaque essai'
  return 'reliée à aucune opération par l’agent'
}

/** Une ligne du registre telle que le bilan la rend (déposés non reliés, à vérifier). */
export type LigneRegistreBilan = {
  statut: StatutJustificatif
  documentRef: string | null
  motif: string | null
  tentativesLiaison?: number
}

/**
 * Ce qu'il reste à faire d'une ligne du registre, sans montant ni libellé bancaire : le document
 * chez le portail, le jour de son opération (`operationLe`, AAAA-MM-JJ, si elle est encore lue) et
 * quand il a été déposé (`deposeLe`, déjà formaté).
 */
export function texteLigneRegistre(
  l: LigneRegistreBilan,
  o: { portail: string; deposeLe: string | null; operationLe: string | null },
): string {
  const document = l.documentRef ? ` (document n° ${l.documentRef})` : ''
  const operation =
    o.operationLe && /^\d{4}-\d{2}-\d{2}$/.test(o.operationLe)
      ? ` à l’opération du ${formaterJour(o.operationLe)}`
      : ' à son opération'
  switch (l.statut) {
    case 'depose':
      return [
        `Déposée dans ${o.portail}${document}${o.deposeLe ? ` le ${o.deposeLe}` : ''}, pas encore reliée${operation} : le prochain passage en écriture la relie si la correspondance est encore sûre ; sinon il constate seulement une liaison faite à la main.`,
        ...(l.tentativesLiaison
          ? [pluriel(l.tentativesLiaison, 'essai de liaison.', 'essais de liaison.')]
          : []),
      ].join(' ')
    case 'incertain':
      return `Dépôt interrompu : vérifier dans ${o.portail} s’il est arrivé, puis le dire sur la fiche de la pièce. Jamais redéposée d’office.`
    case 'orphelin':
      return `Déposée dans ${o.portail}${document}, ${pourquoiOrphelin(l.motif)} : vérifier dans ${o.portail} et relier à la main ce qui manque ; le passage suivant le constate.`
    case 'reserve':
      return `Dépôt dans ${o.portail} en cours.`
    case 'relie':
      return `Déposée dans ${o.portail}${document} et reliée${operation}.`
    case 'present':
      return `Déjà dans ${o.portail} : reconnue sur une opération déjà justifiée, jamais déposée.`
    case 'echec':
      return `Dépôt dans ${o.portail} en échec.`
    case 'annule':
      return `Dépôt dans ${o.portail} abandonné.`
  }
}

// ---------------------------------------------------------------------------------------------
// Le débit du relevé apparié (décision de Pierre-Marie du 13/10/2026, lot 3 de l'audit des tâches)
// ---------------------------------------------------------------------------------------------
// Une opération lue chez le cabinet et la transaction du relevé qui est ce même débit : le bilan
// dit le débit et où en est son rapprochement bancaire ; l'écran du rapprochement bancaire dit où
// se choisit sa pièce. Vocabulaire : le « rapprochement bancaire » relie une opération du relevé
// à un paiement prévu ; le « justificatif » est la pièce d'une opération du cabinet.

/** Le débit du relevé tel que le bilan le rend (`releve` d'une opération). */
export type ReleveVu = {
  transactionId: string
  compte: string
  jour: string
  montantCts: number
  devise: string
  aRapprocher: boolean
  ecartee: boolean
  paiements: readonly { id: string; libelle: string }[]
}

/** Le lien vers l'écran du rapprochement bancaire d'un débit apparié encore à relier. */
export const LIEN_RAPPROCHEMENT_RELEVE = 'Quel paiement prévu règle ce débit ?'

/**
 * Ce que le bilan dit du débit du relevé apparié : le débit (compte, jour, montant), puis le lien
 * vers son rapprochement bancaire s'il reste à faire (`rapprocher`), et les paiements prévus
 * reliés, ou « rien à relier » s'il a été écarté.
 */
export function texteReleve(r: ReleveVu): {
  debit: string
  rapprocher: boolean
  lien: string
  suite: string | null
} {
  const noms = r.paiements.map((p) => p.libelle).join(', ')
  const suite =
    r.paiements.length > 0
      ? `${r.aRapprocher ? 'Paiement relié en partie' : 'Paiement relié'} : ${noms}`
      : r.aRapprocher
        ? null
        : r.ecartee
          ? 'Rapprochement bancaire : rien à relier'
          : 'Rapprochement bancaire fait'
  return {
    debit: `Débit du relevé : ${r.compte}, ${formaterJour(r.jour)}, ${formaterMontant(Math.abs(r.montantCts), r.devise)}`,
    rapprocher: r.aRapprocher,
    lien: LIEN_RAPPROCHEMENT_RELEVE,
    suite,
  }
}

/** Sur l'écran du rapprochement bancaire d'un débit apparié : où se choisit sa pièce. */
export function texteOperationCabinet(portail: string): string {
  return `Opération lue chez ${portail} : sa pièce se choisit dans les justificatifs.`
}

/**
 * Confirmation de « C'est celle-ci » : l'agent dépose et relie au prochain passage en écriture ;
 * quand le débit du relevé apparié reste à rapprocher, le même geste le rapproche du paiement
 * prévu de la pièce s'il lui correspond.
 */
export function confirmationChoisir(o: { portail: string; releveARapprocher: boolean }): string {
  return [
    `Cette pièce est-elle bien celle de cette opération ? L’agent du Mac la déposera dans ${o.portail} et l’y reliera au prochain passage en écriture, après avoir relu l’opération ; rien n’est écrit dans ${o.portail} d’ici là.`,
    ...(o.releveARapprocher
      ? [
          'Le débit du relevé sera aussi rapproché du paiement prévu de la pièce, s’il lui correspond.',
        ]
      : []),
  ].join(' ')
}
